import { Array as A, Option, pipe } from 'effect';
import type { OcrTextMapPage } from '@/domain/documents/queries';

type OcrValue = string | number;
/** A box in OCR units, y at its bottom edge: strings for a word (as the backend sent them), numbers for a field. */
export type OcrBoundingPoly = { x: OcrValue; y: OcrValue; w: OcrValue; h: OcrValue };
export type OcrBox = { boundingPoly: OcrBoundingPoly };
/** The size OCR measured the page at. */
type OcrPageSize = Pick<OcrTextMapPage, 'width' | 'height'>;

/** How a page is displayed: its width with the zoom applied, its height, and the user's rotation only. */
export type DisplayedPageLayout = { width: number; height: number; userRotation: number };

/** An OCR polygon's rectangle in pixels of the displayed page. */
export type DisplayedOcrPolygonRect = { left: number; top: number; width: number; height: number };
export type DisplayedOcrPolygon = DisplayedOcrPolygonRect & { id: string; text: string };

// OCR measures the page before the user's rotation: a page turned an odd number of quarters is matched by its
// OCR height. A rotation that is not a whole number of quarters gives NaN, and so does every polygon.
const ocrSideAlongDisplayedWidth = (size: { width: number; height: number }, userRotation: number) => {
  const quarterTurns = (userRotation / 90) % 2;
  if (quarterTurns === 0) return size.width;
  if (quarterTurns === 1) return size.height;
  return NaN;
};

const parseOcrValue = (value: OcrValue) => parseFloat(String(value));

const pixelsPerOcrUnit = (page: OcrPageSize, layout: DisplayedPageLayout) =>
  layout.width / ocrSideAlongDisplayedWidth(page, layout.userRotation);

const rotateRect = (rect: DisplayedOcrPolygonRect, layout: DisplayedPageLayout): DisplayedOcrPolygonRect => {
  switch (layout.userRotation) {
    case 90:
      return { left: layout.width - rect.height - rect.top, top: rect.left, width: rect.height, height: rect.width };
    case 180:
      return {
        left: layout.width - rect.width - rect.left,
        top: layout.height - rect.height - rect.top,
        width: rect.width,
        height: rect.height,
      };
    case 270:
      return { left: rect.top, top: layout.height - rect.width - rect.left, width: rect.height, height: rect.width };
    default:
      return rect;
  }
};

export const displayedOcrPolygonRect = (
  page: OcrPageSize,
  layout: DisplayedPageLayout,
  box: OcrBoundingPoly,
): DisplayedOcrPolygonRect => {
  const pixels = pixelsPerOcrUnit(page, layout);
  const { x, y, w, h } = box;
  const height = parseOcrValue(h) * pixels;
  // OCR gives the polygon's bottom edge as y.
  return rotateRect(
    {
      left: parseOcrValue(x) * pixels,
      top: parseOcrValue(y) * pixels - height,
      width: parseOcrValue(w) * pixels,
      height,
    },
    layout,
  );
};

const decimalPlaces = (value: OcrValue) => (String(value).split('.')[1] ?? '').length;

/**
 * The OCR box around several OCR boxes, in OCR units, with y as the bottom edge like OCR gives it. The sides are
 * added in whole units of the finest decimal place of the input, so the result has no floating point remainder.
 */
export const ocrBoundingBox = (boxes: A.NonEmptyReadonlyArray<OcrBox>): OcrBoundingPoly => {
  const places = Math.max(...A.flatMap(boxes, ({ boundingPoly: { x, y, w, h } }) => [x, y, w, h].map(decimalPlaces)));
  const unit = 10 ** places;
  const toUnits = (value: OcrValue) => Math.round(parseOcrValue(value) * unit);
  // Written like the backend writes the text map (Python `str(float)`): a whole number keeps its `.0`.
  const fromUnits = (units: number) =>
    Number.isInteger(units / unit) ? (units / unit).toFixed(1) : String(units / unit);
  const edges = A.map(boxes, ({ boundingPoly: { x, y, w, h } }) => ({
    left: toUnits(x),
    right: toUnits(x) + toUnits(w),
    top: toUnits(y) - toUnits(h),
    bottom: toUnits(y),
  }));
  const left = Math.min(...A.map(edges, (edge) => edge.left));
  const top = Math.min(...A.map(edges, (edge) => edge.top));
  const bottom = Math.max(...A.map(edges, (edge) => edge.bottom));
  return {
    x: fromUnits(left),
    y: fromUnits(bottom),
    w: fromUnits(Math.max(...A.map(edges, (edge) => edge.right)) - left),
    h: fromUnits(bottom - top),
  };
};

export const displayedOcrPolygons = (page: OcrTextMapPage, layout: DisplayedPageLayout): DisplayedOcrPolygon[] =>
  page.words.map((word) => ({
    ...displayedOcrPolygonRect(page, layout, word.boundingPoly),
    text: word.text,
    id: word.id,
  }));

/** A rectangle on the screen, as `getBoundingClientRect` gives it. */
type ScreenRect = Pick<DOMRectReadOnly, 'left' | 'top' | 'right' | 'bottom'>;

/** A page as it is shown: where it is on the screen, its OCR page, and how it is laid out. */
export type ShownOcrPage = { rect: ScreenRect; ocrPage: OcrTextMapPage | undefined; layout: DisplayedPageLayout };

/**
 * The texts of the words whose centre is inside the box, in OCR order, taken from the first page with words that
 * does not end above the box; the words of a second page under the same box are left out. The pages go top to
 * bottom, so a first page that starts below the box means no page is under it, and no word is inside it.
 */
export const ocrWordsUnderBox = (box: ScreenRect, pages: ReadonlyArray<ShownOcrPage>): string[] =>
  pipe(
    pages,
    A.findFirst(
      (page): page is ShownOcrPage & { ocrPage: OcrTextMapPage } =>
        page.ocrPage !== undefined && page.ocrPage.words.length > 0 && box.top <= page.rect.bottom,
    ),
    Option.map(({ rect, ocrPage, layout }) =>
      pipe(
        displayedOcrPolygons(ocrPage, layout),
        A.filter((word) => {
          const centreX = rect.left + word.left + word.width / 2;
          const centreY = rect.top + word.top + word.height / 2;
          return box.left <= centreX && centreX <= box.right && box.top <= centreY && centreY <= box.bottom;
        }),
        A.map((word) => word.text),
      ),
    ),
    Option.getOrElse((): string[] => []),
  );
